import { Button, Container, Table, Text } from "@fluidattacks/design";
import type {
  CellContext,
  ColumnDef,
  Row as TableRow,
} from "@tanstack/react-table";
import { StrictMode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { severityApprovalCheckboxButtons } from "./change-severity-formatter";
import type { ISeverityRequestsTableProps } from "./types";

import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { VulnerabilityModal } from "features/vulnerabilities/vulnerability-modal";
import { useTable } from "hooks/use-table";
import { severityFormatter } from "utils/format-helpers";

const SeverityRequestsTable = ({
  vulnsToHandle,
  setVulnsToHandle,
}: ISeverityRequestsTableProps): JSX.Element => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { pathname } = useLocation();
  const [currentRow, setCurrentRow] = useState<IVulnRowAttr>();
  const navigate = useNavigate();

  const openAdditionalInfoModal = useCallback(
    (rowInfo: TableRow<IVulnRowAttr>): void => {
      setCurrentRow(rowInfo.original);
      navigate(`${pathname}/${rowInfo.original.id}`, { replace: true });
    },
    [navigate, pathname],
  );

  const handleReject = (vulnInfo?: IVulnRowAttr): void => {
    if (vulnInfo) {
      const newVulnList: IVulnRowAttr[] = vulnsToHandle.map(
        (vuln: IVulnRowAttr): IVulnRowAttr =>
          vuln.id === vulnInfo.id
            ? {
                ...vuln,
                severityAcceptance:
                  vuln.severityAcceptance === "REJECTED" ? "" : "REJECTED",
              }
            : vuln,
      );
      setVulnsToHandle([...newVulnList]);
    }
  };

  const handleApprove = (vulnInfo?: IVulnRowAttr): void => {
    if (vulnInfo) {
      const newVulnList: IVulnRowAttr[] = vulnsToHandle.map(
        (vuln: IVulnRowAttr): IVulnRowAttr =>
          vuln.id === vulnInfo.id
            ? {
                ...vuln,
                severityAcceptance:
                  vuln.severityAcceptance === "APPROVED" ? "" : "APPROVED",
              }
            : vuln,
      );
      setVulnsToHandle([...newVulnList]);
    }
  };

  const columns = [
    {
      accessorKey: "groupName",
      header: t(
        "severityUpdateRequests.handleRequestApproval.vulnTable.groupName",
      ),
    },
    {
      accessorKey: "where",
      header: t("severityUpdateRequests.handleRequestApproval.vulnTable.where"),
    },
    {
      accessorKey: "specific",
      header: t(
        "severityUpdateRequests.handleRequestApproval.vulnTable.specific",
      ),
      size: 80,
    },
    {
      accessorKey: "severityThreatScore",
      cell: (cell: CellContext<IVulnRowAttr, unknown>): JSX.Element =>
        severityFormatter(cell.getValue() as number),
      header: t(
        "severityUpdateRequests.handleRequestApproval.vulnTable.severity",
      ),
    },
    {
      accessorKey: "proposedSeverityThreatScore",
      cell: (cell: CellContext<IVulnRowAttr, unknown>): JSX.Element =>
        severityFormatter(cell.getValue() as number),
      header: t(
        "severityUpdateRequests.handleRequestApproval.vulnTable.proposedSeverity",
      ),
    },
    {
      accessorKey: "severityAcceptance",
      cell: (cell: CellContext<IVulnRowAttr, unknown>): JSX.Element => {
        const handleSelect = (): void => {
          cell.row.toggleSelected(true);
        };

        return severityApprovalCheckboxButtons(
          cell.row.original,
          handleApprove,
          handleReject,
          handleSelect,
        );
      },
      header: t(
        "severityUpdateRequests.handleRequestApproval.vulnTable.severityAcceptance",
      ),
      size: 200,
    },
  ];

  const formattedVulns = useMemo(
    (): IVulnRowAttr[] =>
      vulnsToHandle.filter(
        ({ groupName, where }): boolean =>
          where.toLowerCase().includes(search.toLowerCase()) ||
          groupName.toLowerCase().includes(search.toLowerCase()),
      ),
    [vulnsToHandle, search],
  );

  const { table } = useTable({
    columns: columns as ColumnDef<IVulnRowAttr>[],
    data: formattedVulns,
    id: "vulns-severity-requests-acceptance-table",
    options: {
      actionsOptions: { onSearch: setSearch },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: true,
        onClick: openAdditionalInfoModal,
      },
    },
    total: formattedVulns.length,
  });

  const handleOnChange = useCallback(
    (newValue: "APPROVED" | "REJECTED"): VoidFunction => {
      return (): void => {
        const newVulnList: IVulnRowAttr[] = vulnsToHandle.map(
          (vuln: IVulnRowAttr): IVulnRowAttr => ({
            ...vuln,
            severityAcceptance: newValue,
          }),
        );
        setVulnsToHandle([...newVulnList]);
        table.toggleAllRowsSelected(true);
      };
    },
    [vulnsToHandle, setVulnsToHandle, table],
  );
  const acceptedVulns = vulnsToHandle.filter(
    (vuln: IVulnRowAttr): boolean => vuln.severityAcceptance === "APPROVED",
  ).length;
  const rejectedVulns = vulnsToHandle.filter(
    (vuln: IVulnRowAttr): boolean => vuln.severityAcceptance === "REJECTED",
  ).length;

  return (
    <StrictMode>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Container gap={0.5} id={"actions-container"}>
              <Button
                icon={"check"}
                onClick={handleOnChange("APPROVED")}
                variant={"ghost"}
              >
                {t(
                  "severityUpdateRequests.handleRequestApproval.vulnTable.approveAll",
                )}
              </Button>
              <Button
                icon={"xmark"}
                onClick={handleOnChange("REJECTED")}
                variant={"ghost"}
              >
                {t(
                  "severityUpdateRequests.handleRequestApproval.vulnTable.rejectAll",
                )}
              </Button>
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
      {acceptedVulns > 0 ? (
        <Text size={"md"}>
          {"You have selected "} <b>{acceptedVulns}</b>
          {" vulnerabilities severity requests to be approved"}
        </Text>
      ) : undefined}
      {rejectedVulns > 0 ? (
        <Text size={"md"}>
          {"You have selected "} <b>{rejectedVulns}</b>
          {" vulnerabilities severity requests to be rejected"}
        </Text>
      ) : undefined}
      {currentRow && pathname.endsWith(currentRow.id) ? (
        <VulnerabilityModal
          cvssVersion={"severityThreatScore"}
          findingId={currentRow.findingId}
          groupName={currentRow.groupName}
          vulnerabilityId={currentRow.id}
        />
      ) : undefined}
    </StrictMode>
  );
};

export { SeverityRequestsTable };
